import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from './base-query';

const vehicleAllAPI = createApi({
    reducerPath: 'vehicleAllAPI',
    baseQuery,
    endpoints: (builder) => ({
        createVehicle: builder.mutation({
            query: ({ body }) => ({
                url: `vehicle`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['vehicle'],
        }),

        getVehicleUploadURL: builder.mutation({
            query: ({ contentType, category }) => ({
                url: `admin/upload`,
                method: 'GET',
                params: {
                    contentType,
                    category,
                },
            }),
            transformResponse: (response) => response.data,
            providesTags: ['vehicle']
        }),
        getAllVehicle: builder.mutation({
            query: ({ 
                page = 0, 
                size = 10, 
                status = 'ALL', 
                search = '',
                makeId = '',
                typeId = '',
                transmission = '',
                fuelType = '',
                numberOfSeats = '',
                minDailyRate = '',
                maxDailyRate = '',
                deliveryAvailable = '',
            }) => ({
                url: `admin/vehicle`,
                method: 'GET',
                params: {
                    page: page + 1,
                    size,
                    status,
                    search,
                    ...(makeId && { makeId }),
                    ...(typeId && { typeId }),
                    ...(transmission && { transmission }),
                    ...(fuelType && { fuelType }),
                    ...(numberOfSeats && { numberOfSeats }),
                    ...(minDailyRate !== '' && { minDailyRate }),
                    ...(maxDailyRate !== '' && { maxDailyRate }),
                    ...(deliveryAvailable !== '' && { deliveryAvailable }),
                },
            }),
            transformResponse: (response) => response,
            providesTags: ['vehicle']
        }),
        getVehicledetails: builder.mutation({
            query: ({id}) => ({
                url: `admin/vehicle/${id}`,
                method: 'GET',
            }),
            providesTags: ['vehicle']
        }),

        getAllTypes: builder.mutation({
            query: () => ({
                url: `vehicle/type`,
                method: 'Get',
            }),
            providesTags:['vehicle']
        }),
        getAllVehicleMakes: builder.mutation({
            query: () => ({
                url: `vehicle/make`,
                method: 'GET',
            }),
        }),
        getAllVehicleModels: builder.mutation({
            query: () => ({
                url: `vehicle/model`,
                method: 'GET',
            }),
            providesTags:['vehicle']
        }),
        getAllVehicleUtilities: builder.mutation ({
            query: () => ({
                url: `vehicle/utility`,
                method: 'GET',
            }),
            providesTags:['vehicle']
        }),

        updateVehicle: builder.mutation({
            query: ({id, body}) => ({
                url: `vehicle/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags:['vehicle']
        }),
        activateVehicle: builder.mutation({
            query:({id}) => ({
                url: `vehicle/${id}/reactivate`,
                method: 'POST',
                body: {},
            }),
        }),
        deactivateVehicle: builder.mutation({
            query: ({id, body}) => ({
                url: `vehicle/${id}/deactivate`,
                method: 'POST',
                body
            }),
        }),
        deleteVehicle: builder.mutation({
            query: ({id, body}) => ({
                url: `vehicle/${id}`,
                method: "DELETE",
                body,
            }),
            invalidatesTags: ["vehicle"],
        }),


        updateVehicleBabySeatPrice: builder.mutation({
            query: ({ id, body }) => ({
                url: `vehicle/${id}/baby-seat-price`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['vehicle'],
        }),

        updateVehicleFuelSurchargePrice: builder.mutation({
            query: ({ id, body }) => ({
                url: `vehicle/${id}/fuel-surcharge-price`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['vehicle'],
        }),

        createVehicleAvailability: builder.mutation({
            query: ({ id, body }) => ({
                url: `vehicle/${id}/availability`,
                method: 'POST',
                body,
            }),
        }),
        updateVehicleAvailability: builder.mutation({
            query: ({ id, body }) => ({
                url: `vehicle/availability/${id}`,
                method: 'PUT',
                body,
            }),
        }),
        deleteVehicleAvailability: builder.mutation({
            query: ({ id }) => ({
                url: `vehicle/availability/${id}`,
                method: 'DELETE',
                body: {},
            }),
        }),
        getVehicleAvailability: builder.mutation({
            query: ({ id, startDate, endDate }) => ({
                url: `vehicle/${id}/availability`,
                method: 'GET',
                params: {
                    startDate,
                    endDate,
                },
            }),
        }),

        getVehicleReviewsByID: builder.mutation({
            query: ({ vehicleId, page= 1, size= 5}) => ({
                url: `reviews/vehicle/${vehicleId}?page=${page}&size=${size}`,
                method: 'GET',
            }),
            transformResponse: (response) => response,
            providesTags: ['vehicle'],
        }),

        // ====================================================================
        // INSIGHTS
        // ====================================================================
        getVehicleInsights: builder.query({
            query: ({ startDate, endDate }) => {
                let apiUrl = `vehicle/insights`;
                if (startDate) {
                    apiUrl += `?startDate=${startDate}`;
                }
                if (endDate) {
                    apiUrl += `${startDate ? '&' : '?'}endDate=${endDate}`;
                }
                return {
                    url: apiUrl,
                    method: 'GET',
                };
            },
            transformResponse: (response) => response.data,
            providesTags: ['vehicleInsights'],
        }),
    }),
});

export const {
    useCreateVehicleMutation,
    useGetVehicleUploadURLMutation,
    useGetAllVehicleMutation,
    useGetVehicledetailsMutation,

    useGetAllTypesMutation,
    useGetAllVehicleMakesMutation,
    useGetAllVehicleModelsMutation,
    useGetAllVehicleUtilitiesMutation,

    useUpdateVehicleMutation,
    useActivateVehicleMutation,
    useDeactivateVehicleMutation,
    useDeleteVehicleMutation,

    useUpdateVehicleBabySeatPriceMutation,
    useUpdateVehicleFuelSurchargePriceMutation,

    useCreateVehicleAvailabilityMutation,
    useUpdateVehicleAvailabilityMutation,
    useDeleteVehicleAvailabilityMutation,
    useGetVehicleAvailabilityMutation,
    // Reviews
    useGetVehicleReviewsByIDMutation,

    // Insights
    useGetVehicleInsightsQuery,
} = vehicleAllAPI;

export default vehicleAllAPI;